import { create } from 'zustand';
import type { DiceResult } from '../components/DiceRoller';
import { useAudioStore } from './audioStore';

type RollFunction = (notation: string) => Promise<DiceResult>;

interface DiceStore {
  rollFn: RollFunction | null;
  isReady: boolean;
  lastResult: DiceResult | null;
  setRollFn: (fn: RollFunction) => void;
  roll: (notation: string) => Promise<DiceResult | null>;
  setLastResult: (result: DiceResult) => void;
}

/** How long the 3D dice get before we roll the numbers without them. */
const DICE_TIMEOUT_MS = 6000;

/**
 * Roll a notation like "1d20+5", "2d6 + 1d4 - 1" or "2d20kh1" without the 3D
 * dice. Used when the 3D roller never started (WebGL trouble, common on
 * iPads) or hangs on a roll; before this, both made a roll silently do
 * nothing, and a hung roll blocked every roll after it.
 */
export function rollWithoutDice(notation: string): DiceResult {
  const expr = notation.replace(/\(.*?\)/g, '').replace(/\s+/g, '').toLowerCase();
  const rolls: { sides: number; value: number }[] = [];
  let total = 0;
  const term = /([+-]?)(\d*)d(\d+)(k[hl]\d+)?|([+-]?)(\d+)/g;
  let m: RegExpExecArray | null;
  while ((m = term.exec(expr))) {
    if (m[3]) {
      const sign = m[1] === '-' ? -1 : 1;
      const count = Math.min(100, parseInt(m[2] || '1', 10));
      const sides = parseInt(m[3], 10);
      const values = Array.from({ length: count }, () => Math.floor(Math.random() * sides) + 1);
      for (const value of values) rolls.push({ sides, value });
      let kept = values;
      if (m[4]) {
        const n = parseInt(m[4].slice(2), 10);
        const sorted = [...values].sort((a, b) => a - b);
        kept = m[4][1] === 'h' ? sorted.slice(-n) : sorted.slice(0, n);
      }
      total += sign * kept.reduce((a, b) => a + b, 0);
    } else if (m[6]) {
      total += (m[5] === '-' ? -1 : 1) * parseInt(m[6], 10);
    }
  }
  return { notation, rolls, total };
}

// Module-level lock — prevents re-entrant / concurrent rolls.
// Synchronous boolean check is atomic in single-threaded JS.
let _rolling = false;

export const useDiceStore = create<DiceStore>((set, get) => ({
  rollFn: null,
  isReady: false,
  lastResult: null,

  setRollFn: (fn) => set({ rollFn: fn, isReady: true }),

  roll: async (notation) => {
    const { rollFn } = get();
    if (!rollFn) {
      console.warn('[DiceStore] 3D dice not ready; rolling without them');
      const result = rollWithoutDice(notation);
      set({ lastResult: result });
      return result;
    }

    // Hard lock: drop concurrent calls while a roll is in progress.
    // Sequential awaits (advantage/disadvantage) still work because
    // the lock clears before the next `await roll()` resumes.
    if (_rolling) {
      console.warn('[DiceStore] Roll already in progress, dropping');
      return null;
    }
    _rolling = true;

    try {
      // Trigger dice roll sound
      useAudioStore.getState().playDiceSound();

      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), DICE_TIMEOUT_MS); });
      const result = (await Promise.race([rollFn(notation), timeout])) ?? (() => {
        console.warn('[DiceStore] 3D dice timed out; rolling without them');
        return rollWithoutDice(notation);
      })();
      clearTimeout(timer);
      set({ lastResult: result });
      return result;
    } catch (err) {
      console.error('[DiceStore] 3D roll failed; rolling without dice:', err);
      const result = rollWithoutDice(notation);
      set({ lastResult: result });
      return result;
    } finally {
      _rolling = false;
    }
  },

  setLastResult: (result) => set({ lastResult: result }),
}));
